import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { ALLOWED_TYPES, putToGcs, useCreateUpload, validateFile } from '../../api/uploads.js';

export interface CommittedAttachment {
  objectName: string;
  contentType: string;
  bytes: number;
  filename: string;
}

interface InflightUpload {
  id: number;
  filename: string;
  contentType: string;
  bytes: number;
  loaded: number;
  abort: AbortController;
  error?: string;
}

interface Props {
  tenantId: string;
  attachments: CommittedAttachment[];
  onChange: (next: CommittedAttachment[]) => void;
  onInflightChange: (count: number) => void;
}

const SOFT_FILE_WARNING_THRESHOLD = 5;

export function AttachmentsStep({ tenantId, attachments, onChange, onInflightChange }: Props) {
  const createUpload = useCreateUpload();
  const [dragOver, setDragOver] = useState(false);
  const [inflight, setInflight] = useState<InflightUpload[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inflightIdRef = useRef(0);

  const activeCount = useMemo(
    () => inflight.filter((u) => !u.error).length,
    [inflight],
  );
  useEffect(() => {
    onInflightChange(activeCount);
  }, [activeCount, onInflightChange]);

  function patchInflight(id: number, p: Partial<InflightUpload>) {
    setInflight((prev) => prev.map((u) => (u.id === id ? { ...u, ...p } : u)));
  }

  function removeInflight(id: number) {
    setInflight((prev) => prev.filter((u) => u.id !== id));
  }

  async function uploadOne(file: File) {
    const v = validateFile(file);
    if (!v.ok) {
      const id = ++inflightIdRef.current;
      setInflight((prev) => [
        ...prev,
        {
          id,
          filename: file.name,
          contentType: file.type,
          bytes: file.size,
          loaded: 0,
          abort: new AbortController(),
          error: v.reason,
        },
      ]);
      return;
    }
    const id = ++inflightIdRef.current;
    const entry: InflightUpload = {
      id,
      filename: file.name,
      contentType: file.type,
      bytes: file.size,
      loaded: 0,
      abort: new AbortController(),
    };
    setInflight((prev) => [...prev, entry]);
    try {
      const grant = await createUpload.mutateAsync({ tenantId, file });
      await putToGcs({
        uploadUrl: grant.uploadUrl,
        file,
        signal: entry.abort.signal,
        onProgress: (loaded) => patchInflight(id, { loaded }),
      });
      // Commit to parent.
      onChange([
        ...attachments,
        {
          objectName: grant.objectName,
          contentType: file.type,
          bytes: file.size,
          filename: file.name,
        },
      ]);
      removeInflight(id);
    } catch (e) {
      const reason = (e as Error).name === 'AbortError' ? 'Annulé' : 'Échec';
      patchInflight(id, { error: reason });
    }
  }

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    for (const f of files) void uploadOne(f);
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(true);
  }
  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
  }
  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    for (const f of files) void uploadOne(f);
  }

  function removeCommitted(idx: number) {
    onChange(attachments.filter((_, i) => i !== idx));
  }

  const total = attachments.length + inflight.filter((u) => !u.error).length;

  return (
    <article className="invoice-detail__card wizard-card">
      <div
        className={`upload-dropzone${dragOver ? ' upload-dropzone--hover' : ''}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        data-testid="attachments-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          multiple
          onChange={onPick}
          style={{ display: 'none' }}
          data-testid="attachments-input"
        />
        <div className="upload-prompt">
          <div className="upload-prompt__icon" aria-hidden="true">
            ↓
          </div>
          <div className="upload-prompt__title display">Glissez des pièces jointes</div>
          <div className="upload-prompt__sub muted">
            ou cliquez pour parcourir · PDF / PNG / JPG / ZIP · 25 Mo max par fichier · optionnel
          </div>
        </div>
      </div>

      {total >= SOFT_FILE_WARNING_THRESHOLD ? (
        <div className="muted" data-testid="attachments-soft-warning" style={{ fontSize: 12 }}>
          Vous avez ajouté {total} fichiers. Vérifiez que toutes les pièces sont nécessaires.
        </div>
      ) : null}

      {attachments.length > 0 || inflight.length > 0 ? (
        <ul className="attachments-list" data-testid="attachments-list">
          {attachments.map((a, i) => (
            <li
              key={`done-${a.objectName}`}
              className="attachments-list__item"
              data-testid={`attachment-done-${i}`}
            >
              <div className="attachments-list__name">{a.filename}</div>
              <div className="muted attachments-list__meta">
                {(a.bytes / (1024 * 1024)).toFixed(2)} Mo · {a.contentType}
              </div>
              <button
                type="button"
                className="comment__delete"
                onClick={() => removeCommitted(i)}
                aria-label={`Retirer ${a.filename}`}
                data-testid={`attachment-remove-${i}`}
              >
                ×
              </button>
            </li>
          ))}
          {inflight.map((u) => {
            const pct = u.bytes > 0 ? Math.round((u.loaded / u.bytes) * 100) : 0;
            return (
              <li
                key={`up-${u.id}`}
                className="attachments-list__item"
                data-testid={`attachment-inflight-${u.id}`}
              >
                <div className="attachments-list__name">{u.filename}</div>
                <div className="muted attachments-list__meta">
                  {u.error
                    ? u.error
                    : `${pct}% · ${(u.loaded / (1024 * 1024)).toFixed(1)} / ${(u.bytes / (1024 * 1024)).toFixed(1)} Mo`}
                </div>
                {u.error ? (
                  <button
                    type="button"
                    className="comment__delete"
                    onClick={() => removeInflight(u.id)}
                    aria-label={`Retirer ${u.filename}`}
                    data-testid={`attachment-inflight-remove-${u.id}`}
                  >
                    ×
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => u.abort.abort()}
                    data-testid={`attachment-cancel-${u.id}`}
                  >
                    Annuler
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </article>
  );
}
